import { translate } from "app/language/service"
import { IRootState, NOT_SAVED_ID } from "app/model"
import { getStore } from "app/store/store"
import { getActiveAppContext } from "app/tools/common"
import { DEFAULT_RADIUS_VALUE } from "coordinate/model"
import { formEventModule } from "form/event"
import { refreshInventory } from "form/inventory/tools/common"
import { DISTANCE_UNITS } from "form/map/model"
import { refreshGeoTargetsMap } from "form/map/tools/geo-targets"
import {
  getCoordinatesPolygons,
  startUpdatingGeoTargetsFormOnMap,
  stopUpdatingGeoTargetsFormOnMap
} from "form/map/tools/geo-targets-coordinate"
import {
  FORM_EVENTS,
  GEO_TARGETS_FORM_TABS,
  IForm,
  IFormChange,
  IFormCoordinate,
  IGeoTargetsForm,
  IGeoTargetsSelection
} from "form/model"
import { formActionCreator } from "form/store/actions"
import { getAppForm, getForm } from "form/tools/common"
import { Feature, MultiPolygon } from "geojson"
import { geometrySVC } from "geometry/service"
import { getMultiPolygonIntersection } from "geometry/tools/common"
import { IGeoShape } from "shape/model"
import { getUser } from "user/tools/common"
import { deleteCoordinates, getCoordinateChange, getRadiusInUserUnit, hasCoordinates } from "./geo-targets-coordinates"
import { deleteShapes, getShapesChanges, hasShapes } from "./geo-targets-shapes"

export function deleteSelectedGeoTargets(): Promise<void> {
  return new Promise<void>(resolve => {
    const {
      geoTargets: { cities, coordinates, postalCodes, states, countries, metropolitans }
    } = getForm()
    const {
      isCreation,
      toUpdateEntityId,
      selection: { cityIds, coordinateIds, postalCodeIds, stateIds, countryIds, metropolitanIds }
    } = getGeoTargetsForm()
    const promises: Array<Promise<void>> = []
    if (coordinateIds.length) {
      if (!isCreation) {
        if (coordinateIds.includes(toUpdateEntityId)) {
          // the coordinate that is currently edited will be deleted
          stopUpdatingGeoTargetsForm()
        }
      }
      promises.push(deleteCoordinates(coordinates.filter(c => coordinateIds.includes(c.id))))
    }
    if (
      cityIds.length > 0 ||
      postalCodeIds.length > 0 ||
      stateIds.length > 0 ||
      countryIds.length > 0 ||
      metropolitanIds.length > 0
    ) {
      promises.push(
        deleteShapes([
          ...(cityIds.length > 0 ? cities.filter(c => cityIds.includes(c.external_id)) : []),
          ...(postalCodeIds.length > 0 ? postalCodes.filter(p => postalCodeIds.includes(p.postal_code)) : []),
          ...(stateIds.length > 0 ? states.filter(s => stateIds.includes(s.state_shape_id)) : []),
          ...(countryIds.length > 0 ? countries.filter(c => countryIds.includes(c.country_shape_id)) : []),
          ...(metropolitanIds.length > 0 ? metropolitans.filter(m => metropolitanIds.includes(m.shape_id)) : [])
        ])
      )
    }
    if (promises.length === 0) {
      return resolve()
    }
    setGeoTargetsForm({ isDeletingSelection: true })
    Promise.all(promises)
      .then(() => {
        setGeoTargetsForm({ isDeletingSelection: false })
        refreshInventory()
        resolve()
      })
      .catch(error => {
        setGeoTargetsForm({ isDeletingSelection: false })
        refreshInventory()
        console.error(error)
      })
  })
}

export function getGeoTargetsChanges(form: IForm): IFormChange[] {
  return [getCoordinateChange(form), ...getShapesChanges(form)]
}

export function getGeoTargetsFormSafely(state?: IRootState): IGeoTargetsForm | null {
  if (!state) {
    state = getStore().getState()
  }
  const context = getActiveAppContext()
  if (context === null) {
    return null
  }
  return getAppForm(state).geoTargetsForm
}

export function getGeoTargetsForm(state?: IRootState): IGeoTargetsForm {
  const form = getGeoTargetsFormSafely(state)
  if (form === null) {
    throw Error("No context is active")
  }
  return form
}

export function setGeoTargetsForm(form: Partial<IGeoTargetsForm>): void {
  const context = getActiveAppContext()
  if (context === null) {
    return
  }
  getStore().dispatch(formActionCreator.setGeoTargetsForm(context, form))
}

export function setGeoTargetsFormSelection(selection: Partial<IGeoTargetsSelection>): void {
  const context = getActiveAppContext()
  if (context === null) {
    return
  }
  getStore().dispatch(formActionCreator.setGeoTargetsFormSelection(context, selection))
}

export function startUpdatingGeoTargetsForm(coordinate: IFormCoordinate): void {
  setGeoTargetsForm({
    isCreation: false,
    latitude: String(coordinate.latitude),
    longitude: String(coordinate.longitude),
    name: coordinate.name,
    radius: String(getRadiusInUserUnit(coordinate.radius)),
    activeTab: GEO_TARGETS_FORM_TABS.COORDINATE,
    toUpdateEntityId: coordinate.id
  })
  startUpdatingGeoTargetsFormOnMap(coordinate)
}

export function stopUpdatingGeoTargetsForm(activeTab?: GEO_TARGETS_FORM_TABS): void {
  setGeoTargetsForm({
    isCreation: true,
    latitude: null,
    longitude: null,
    name: "",
    radius: DEFAULT_RADIUS_VALUE,
    toUpdateEntityId: NOT_SAVED_ID,
    activeTab: activeTab ? activeTab : GEO_TARGETS_FORM_TABS.COORDINATE
  })
  stopUpdatingGeoTargetsFormOnMap()
}

export function stopUpdatingGeoTargetsFormAfterDeletionIfNeeded(deletedIds: number[]): void {
  const geoTargetForm = getGeoTargetsForm()
  if (geoTargetForm.activeTab !== GEO_TARGETS_FORM_TABS.COORDINATE || geoTargetForm.isCreation) {
    return
  }
  if (!deletedIds.includes(geoTargetForm.toUpdateEntityId)) {
    return
  }
  stopUpdatingGeoTargetsForm()
}

export function activateGeoTargetsTab(toActivateTab: GEO_TARGETS_FORM_TABS): void {
  const geoTargetsForm = getGeoTargetsForm()
  if (geoTargetsForm.activeTab === toActivateTab) {
    return
  }
  setGeoTargetsForm({ activeTab: toActivateTab })
  refreshGeoTargetsMap(toActivateTab)
}

export function isValidLongitude(longitude: string | null): boolean {
  if (longitude === null || longitude === "" || isNaN(longitude as any)) {
    return false
  }
  const longitudeNumber = Number(longitude)
  return longitudeNumber >= -180 && longitudeNumber <= 180
}

export function isValidLatitude(latitude: string | null): boolean {
  if (latitude === null || latitude === "" || isNaN(latitude as any)) {
    return false
  }
  const latitudeNumber = Number(latitude)
  return latitudeNumber >= -90 && latitudeNumber <= 90
}

export function isValidRadius(radius: string | null): boolean {
  if (radius === null || radius === "" || isNaN(radius as any)) {
    return false
  }
  const radiusNumber = Number(radius)
  return radiusNumber >= 0 && Number.isInteger(radiusNumber)
}

export function getDistanceUnit(): DISTANCE_UNITS {
  return getUser().ui_settings.distance_unit as DISTANCE_UNITS
}

export function getDistanceUnitTitle(): string {
  return translate(`label.distance-unit.${getDistanceUnit()}`)
}

export function isImperialDistances(distanceUnit?: DISTANCE_UNITS): boolean {
  switch (distanceUnit || getDistanceUnit()) {
    case DISTANCE_UNITS.MILLIMETERS:
    case DISTANCE_UNITS.CENTIMETERS:
    case DISTANCE_UNITS.METERS:
    case DISTANCE_UNITS.KILOMETERS: {
      return false
    }
    default: {
      return true
    }
  }
}

export function getDistanceKind(): "metric" | "imperial" {
  return isImperialDistances() ? "imperial" : "metric"
}

export function getDistanceInUserUnit(distanceInKm: number): number {
  const userUnit = getDistanceUnit()
  if (userUnit === DISTANCE_UNITS.KILOMETERS) {
    return distanceInKm
  }
  return geometrySVC.convertLength(distanceInKm, userUnit)
}

export function setIntersectionGeoTargetFeature(avoidNotify?: boolean): void {
  const form = getForm()
  const intersectionFeature = getGeoTargetIntersectionFeature(form)
  const hasNoIntersectionError = intersectionFeature === false
  setIntersectionErrorInStore(hasNoIntersectionError)
  if (avoidNotify) {
    return
  }
  formEventModule.notify(FORM_EVENTS.GEO_TARGET_CHANGE, {
    form,
    intersectionFeature: typeof intersectionFeature === "object" ? intersectionFeature : null,
    hasNoIntersectionError
  })
}

/**
 * @returns false if coordinates and shapes have no intersection, else true (means all is correct)
 */
export function getGeoTargetIntersectionFeature(form?: IForm): Feature<MultiPolygon> | boolean {
  if (!form) {
    form = getForm()
  }
  const coordinates = form.geoTargets.coordinates
  const shapes: IGeoShape[] = [
    ...form.geoTargets.cities,
    ...form.geoTargets.countries,
    ...form.geoTargets.states,
    ...form.geoTargets.postalCodes
  ]
  if (coordinates.length === 0 && shapes.length === 0) {
    return true
  }
  if (coordinates.length === 0) {
    return geometrySVC.getMultiPolygonFeature(
      shapes.map(s => s.shape),
      {}
    )
  }
  if (shapes.length === 0) {
    return geometrySVC.getMultiPolygonFeature(getCoordinatesPolygons(), {})
  }
  const intersectFeature = getMultiPolygonIntersection(
    geometrySVC.getMultiPolygonFeature(
      shapes.map(s => s.shape),
      {}
    ),
    geometrySVC.getMultiPolygonFeature(getCoordinatesPolygons(), {})
  )
  return intersectFeature ? intersectFeature : false
}

function setIntersectionErrorInStore(hasNoIntersectionError: boolean): void {
  const form = getForm()
  if (hasNoIntersectionError) {
    if (!getGeoTargetsForm().noIntersectionError) {
      getStore().dispatch(formActionCreator.setGeoTargetsNoIntersectionError(form.context.appContext, true))
    }
  } else if (getGeoTargetsForm().noIntersectionError) {
    getStore().dispatch(formActionCreator.setGeoTargetsNoIntersectionError(form.context.appContext, false))
  }
}

export function hasGeoTargets(form: IForm): boolean {
  return hasCoordinates(form) || hasShapes(form)
}
